import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AdminAuthProvider, useAdminAuth } from "./AdminAuthContext";
import { AdminLayout } from "./AdminLayout";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAccounts from "./pages/AdminAccounts";
import AdminAccountDetail from "./pages/AdminAccountDetail";
import AdminLists from "./pages/AdminLists";
import AdminPeo from "./pages/AdminPeo";
import AdminPeoProfiles from "./pages/AdminPeoProfiles";
import AdminScheduling from "./pages/AdminScheduling";
import AdminBotAccess from "./pages/AdminBotAccess";
import AdminRenewals from "./pages/AdminRenewals";
import AdminCoEmployer from "./pages/AdminCoEmployer";
import AdminSequences from "./pages/AdminSequences";
import AdminPipeline from "./pages/AdminPipeline";
import AdminCampaigns from "./pages/AdminCampaigns";
import AdminPlanBuilder from "./pages/AdminPlanBuilder";
import AdminPlanScenarios from "./pages/AdminPlanScenarios";
import { AdminPlanLeads, AdminPlanLeadDetail } from "./pages/AdminPlanLeads";
import AdminPlanSummary from "./pages/AdminPlanSummary";
import AdminData from "./pages/AdminData";
import AdminHarvestTimeline from "./pages/AdminHarvestTimeline";
import AdminHarvestCadences from "./pages/AdminHarvestCadences";
import AdminHarvestAccounts from "./pages/AdminHarvestAccounts";
import AdminHarvestFulfillment from "./pages/AdminHarvestFulfillment";
import AdminHarvestApprovals from "./pages/AdminHarvestApprovals";
import AdminHarvestKpis from "./pages/AdminHarvestKpis";

function Protected({ children }) {
  const { user, checked } = useAdminAuth();
  const location = useLocation();

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0E1A13]">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-cream/40">Checking session…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  return <AdminLayout>{children}</AdminLayout>;
}

function NoIndex() {
  useEffect(() => {
    document.title = "Growth Desk — OneHarvestCo. Benefits";
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);
  return null;
}

export default function AdminRoutes() {
  return (
    <AdminAuthProvider>
      <NoIndex />
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route path="" element={<Protected><AdminDashboard /></Protected>} />
        <Route path="accounts" element={<Protected><AdminAccounts /></Protected>} />
        <Route path="accounts/:accountId" element={<Protected><AdminAccountDetail /></Protected>} />
        <Route path="lists" element={<Protected><AdminLists /></Protected>} />
        <Route path="peo" element={<Protected><AdminPeo /></Protected>} />
        <Route path="peo-partners" element={<Protected><AdminPeoProfiles /></Protected>} />
        <Route path="scheduling" element={<Protected><AdminScheduling /></Protected>} />
        <Route path="bot-access" element={<Protected><AdminBotAccess /></Protected>} />
        <Route path="renewals" element={<Protected><AdminRenewals /></Protected>} />
        <Route path="co-employer" element={<Protected><AdminCoEmployer /></Protected>} />
        <Route path="sequences" element={<Protected><AdminSequences /></Protected>} />
        <Route path="pipeline" element={<Protected><AdminPipeline /></Protected>} />
        <Route path="campaigns" element={<Protected><AdminCampaigns /></Protected>} />
        <Route path="data" element={<Protected><AdminData /></Protected>} />
        <Route path="plan-builder" element={<Protected><AdminPlanBuilder /></Protected>} />
        <Route path="plan-builder/scenarios" element={<Protected><AdminPlanScenarios /></Protected>} />
        <Route path="plan-builder/scenarios/:id" element={<Protected><AdminPlanBuilder /></Protected>} />
        <Route path="plan-builder/scenarios/:scenarioId/summary" element={<Protected><AdminPlanSummary /></Protected>} />
        <Route path="plan-builder/leads" element={<Protected><AdminPlanLeads /></Protected>} />
        <Route path="plan-builder/leads/:leadId" element={<Protected><AdminPlanLeadDetail /></Protected>} />
        <Route path="harvest" element={<Protected><AdminHarvestTimeline /></Protected>} />
        <Route path="harvest/cadences" element={<Protected><AdminHarvestCadences /></Protected>} />
        <Route path="harvest/accounts" element={<Protected><AdminHarvestAccounts /></Protected>} />
        <Route path="harvest/fulfillment" element={<Protected><AdminHarvestFulfillment /></Protected>} />
        <Route path="harvest/approvals" element={<Protected><AdminHarvestApprovals /></Protected>} />
        <Route path="harvest/kpis" element={<Protected><AdminHarvestKpis /></Protected>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}
